import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import CallModal from "@/components/feature/CallModal";

export interface ScheduledCallItem {
  id: string | number;
  leadName: string;
  company?: string;
  phone: string;
  time: string;
  timeSort: string;
  purpose: string;
  agent: string;
  notes?: string;
  status?: string;
  lead_id?: string | number;
  leadId?: string | number;
}

function getCountdown(timeSort: string): { text: string; urgent: boolean; overdue: boolean } | null {
  if (!timeSort) return null;
  const target = new Date(timeSort).getTime();
  const now = Date.now();
  const diffMs = target - now;

  if (diffMs < 0) {
    return { text: "Overdue", urgent: false, overdue: true };
  }

  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);

  if (diffMin < 30) {
    return { text: `In ${diffMin}m`, urgent: true, overdue: false };
  }
  if (diffMin < 60) {
    return { text: `In ${diffMin}m`, urgent: false, overdue: false };
  }
  const remainMin = diffMin % 60;
  return { text: `In ${diffHr}h ${remainMin}m`, urgent: false, overdue: false };
}

interface Props {
  scheduledCalls?: ScheduledCallItem[];
  loading?: boolean;
  onReload?: () => void;
  onScheduleCall: () => void;
}

export default function ScheduledTab({
  scheduledCalls = [],
  loading = false,
  onReload,
  onScheduleCall,
}: Props) {
  const { showToast } = useToast();
  const [, setNow] = useState(Date.now());
  const [activeCall, setActiveCall] = useState<ScheduledCallItem | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const handleCallNow = (call: ScheduledCallItem) => {
    setActiveCall(call);
  };

  const handleCancelCall = async (call: ScheduledCallItem) => {
    if (!window.confirm(`Cancel scheduled call with ${call.leadName}?`)) return;
    try {
      await fetchApi(`/calls/scheduled/${call.id}`, { method: "DELETE" });
      showToast("Scheduled call cancelled", "success");
      if (onReload) onReload();
    } catch (err: any) {
      showToast(err?.message || "Failed to cancel call", "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500 font-medium">
          {scheduledCalls.length} call{scheduledCalls.length === 1 ? "" : "s"} scheduled
        </div>
        <div className="flex items-center gap-2">
          {onReload && (
            <button
              onClick={onReload}
              title="Refresh Scheduled Calls"
              className="p-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-600 transition-colors cursor-pointer"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
            </button>
          )}
          <button
            onClick={onScheduleCall}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-xs"
          >
            <i className="ri-calendar-event-line" /> Schedule Call
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80">
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Lead
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Phone
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Scheduled Time
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Countdown
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Purpose
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Assigned Agent
                </th>
                <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {scheduledCalls.map((call) => {
                const cd = getCountdown(call.timeSort);
                return (
                  <tr
                    key={call.id}
                    className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-gray-900 leading-snug">{call.leadName}</p>
                        {call.company && (
                          <p className="text-xs text-gray-500 mt-0.5">{call.company}</p>
                        )}
                        {call.notes && (
                          <p className="text-[11px] text-gray-400 truncate max-w-xs mt-0.5">
                            {call.notes}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-700 font-medium">{call.phone}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-800 font-semibold">{call.time}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {cd && (
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                            cd.overdue
                              ? "bg-red-100 text-red-700 border border-red-200"
                              : cd.urgent
                              ? "bg-amber-100 text-amber-800 border border-amber-200"
                              : "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}
                        >
                          {cd.text}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {call.purpose || "Follow-up"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-700 font-medium">{call.agent || "Agent"}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleCallNow(call)}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors flex items-center gap-1 cursor-pointer shadow-xs active:scale-98"
                        >
                          <i className="ri-phone-fill text-xs" /> Call Now
                        </button>
                        <button
                          onClick={() => handleCancelCall(call)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                          title="Cancel Scheduled Call"
                        >
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {scheduledCalls.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-calendar-event-line text-3xl text-indigo-400" />
                    </div>
                    <p className="text-lg font-semibold text-gray-700 mb-1">No scheduled calls</p>
                    <p className="text-sm text-gray-500 mb-4">
                      Schedule a call with a lead to see upcoming appointments here
                    </p>
                    <button
                      onClick={onScheduleCall}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors cursor-pointer"
                    >
                      <i className="ri-calendar-event-line" /> Schedule a Call
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Direct In-CRM Call Modal */}
      {activeCall && (
        <CallModal
          isOpen={!!activeCall}
          onClose={() => setActiveCall(null)}
          contactName={activeCall.leadName}
          phone={activeCall.phone}
          agentName={activeCall.agent}
          leadId={activeCall.leadId || activeCall.lead_id}
          onCallLogged={() => {
            if (onReload) onReload();
            setActiveCall(null);
          }}
        />
      )}
    </div>
  );
}